import { Form } from 'components/organisms';
import { Application, SettingsTemplate } from 'components/templates';
import {
  Advance,
  Artist,
  BlobConnection,
  Channel,
  ChannelVideo,
  ClientAccount,
  ClientUser,
  Contacts,
  Contract,
  ContractTemplate,
  Cost,
  DevTools,
  DigitalDeliveryErrors,
  Email,
  Event,
  FtpConnection,
  GlobalRaports,
  Home,
  Income,
  Label,
  LabelReports,
  Logs,
  Merch,
  MusicProductDigital,
  MusicProductPhysical,
  Notification,
  PeriodReports,
  Provider,
  SaleChannel,
  ServiceCode,
  Settings,
  Settlement,
  SettlementOrder,
  SettlementPeriod,
  SftpConnection,
  Statements,
  Statistics,
  Thread,
  Track,
  Unpaid,
  User,
  Video,
} from 'pages/private';

import DevToolsTemplate from 'components/templates/dev-tools/DevToolsTemplate';
import {
  Email as ConfirmEmail,
  Health,
  LabelForm,
  Login,
  Password,
  ServiceLogin,
  TwoFactor,
  TwoFactorRecovery,
} from 'pages/public';
import LogoProvider from 'providers/LogoProvider';
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useLocation,
} from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';

const NavigateToReportContractEarnings = () => {
  const location = useLocation();
  return <Navigate to='contractEarnings' replace state={location.state} />;
};

const NavigateToStatementSummaryRows = () => {
  const location = useLocation();
  return <Navigate to='summaryRows' replace state={location.state} />;
};

export const routes = createBrowserRouter([
  {
    path: '/',
    element: (
      <LogoProvider>
        <ProtectedRoute>
          <Application />
        </ProtectedRoute>
      </LogoProvider>
    ),
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: 'statistics',
        element: <Statistics />,
      },
      {
        path: 'merch',
        children: [
          {
            index: true,
            element: <Merch.List />,
          },
          {
            path: ':id',
            element: <Merch.Individual />,
            children: Form.Merch.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <Merch.Edit />,
            children: Form.Merch.getRouterTabs(),
          },
          {
            path: 'create',
            element: <Merch.Create />,
            children: Form.Merch.getRouterTabs(),
          },
        ],
      },

      {
        path: 'label-reports',
        children: [
          {
            index: true,
            element: <LabelReports.List />,
          },
        ],
      },
      {
        path: 'period-reports',
        children: [
          {
            index: true,
            element: <PeriodReports.List />,
          },
        ],
      },

      {
        path: 'track',
        children: [
          {
            index: true,
            element: <Track.List />,
          },
          {
            path: ':id',
            element: <Track.Individual />,
            children: Form.Track.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <Track.Edit />,
            children: Form.Track.getRouterTabs(),
          },
          {
            path: 'create',
            element: <Track.Create />,
            children: Form.Track.getRouterTabs(),
          },
        ],
      },
      {
        path: 'video',
        children: [
          {
            index: true,
            element: <Video.List />,
          },
          {
            path: ':id',
            element: <Video.Individual />,
            children: Form.Video.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <Video.Edit />,
            children: Form.Video.getRouterTabs(),
          },
          {
            path: 'create',
            element: <Video.Create />,
            children: Form.Video.getRouterTabs(),
          },
        ],
      },

      {
        path: 'music-product-physical',
        children: [
          {
            index: true,
            element: <MusicProductPhysical.List />,
          },
          {
            path: ':id',
            element: <MusicProductPhysical.Individual />,
            children: Form.MusicProductPhysical.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <MusicProductPhysical.Edit />,
            children: Form.MusicProductPhysical.getRouterTabs(),
          },
          {
            path: 'create',
            element: <MusicProductPhysical.Create />,
            children: Form.MusicProductPhysical.getRouterTabs(),
          },
        ],
      },
      {
        path: 'statements',
        children: [
          {
            index: true,
            element: <Statements.List />,
          },
          {
            path: ':id',
            element: <Statements.Individual />,
            children: [
              {
                index: true,
                element: <NavigateToStatementSummaryRows />,
              },
              ...(Form.ExchangeRates.getRouterTabs?.() ?? []),
            ],
          },
        ],
      },
      {
        path: 'settlement-order',
        children: [
          {
            index: true,
            element: <SettlementOrder.List />,
          },
        ],
      },
      {
        path: 'contacts-list',
        children: [
          {
            index: true,
            element: <Contacts.List />,
          },
        ],
      },
      {
        path: 'notifications',
        children: [
          {
            index: true,
            element: <Notification.List />,
          },
          {
            path: 'archive',
            children: [
              {
                index: true,
                element: <Notification.Archive />,
              },
            ],
          },
        ],
      },
      {
        path: 'artist',
        children: [
          {
            index: true,
            element: <Artist.List />,
          },
          {
            path: ':id',
            element: <Artist.Individual />,
            children: Form.Artist.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <Artist.Edit />,
            children: Form.Artist.getRouterTabs(),
          },
          {
            path: 'create',
            element: <Artist.Create />,
            children: Form.Artist.getRouterTabs(),
          },
        ],
      },
      {
        path: 'label',
        children: [
          {
            index: true,
            element: <Label.List />,
          },
          {
            path: ':id',
            element: <Label.Individual />,
            children: Form.Label.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <Label.Edit />,
            children: Form.Label.getRouterTabs(),
          },
          {
            path: 'create',
            element: <Label.Create />,
            children: Form.Label.getRouterTabs(),
          },
          {
            path: ':id/reports/:reportId',
            element: <LabelReports.Individual />,
            children: [
              {
                index: true,
                element: <NavigateToReportContractEarnings />,
              },
              ...(Form.LabelReport.getRouterTabs?.() ?? []),
            ],
          },
          {
            path: ':id/advance/:advanceId',
            element: <Advance.Individual />,
            children: Form.AdvanceLabelForm.getRouterTabs(),
          },
          {
            path: ':id/advance/create',
            element: <Advance.CreateAdvance />,
            children: Form.AdvanceLabelForm.getRouterTabs(),
          },
          {
            path: ':id/advance/edit/:advanceId',
            element: <Advance.EditAdvance />,
            children: Form.AdvanceLabelForm.getRouterTabs(),
          },
          {
            path: ':id/contract',
            children: [
              {
                path: ':contractId',
                element: <Contract.Individual />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'edit/:contractId',
                element: <Contract.Edit />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'create',
                element: <Contract.Create />,
                children: Form.Contract.getRouterTabs(),
              },
            ],
          },
          {
            path: ':id/cost',
            children: [
              {
                path: ':cashFlowId',
                element: <Cost.Individual />,
                children: Form.CashFlow.getRouterTabs(),
              },
              {
                path: 'edit/:cashFlowId',
                element: <Cost.Edit />,
                children: Form.CashFlow.getRouterTabs(),
              },
              {
                path: 'create',
                element: <Cost.Create />,
                children: Form.CashFlow.getRouterTabs(),
              },
            ],
          },
          {
            path: ':id/income',
            children: [
              {
                path: ':cashFlowId',
                element: <Income.Individual />,
                children: Form.CashFlow.getRouterTabs(),
              },
              {
                path: 'edit/:cashFlowId',
                element: <Income.Edit />,
                children: Form.CashFlow.getRouterTabs(),
              },
              {
                path: 'create',
                element: <Income.Create />,
                children: Form.CashFlow.getRouterTabs(),
              },
            ],
          },
          {
            path: ':id/threads',
            children: [
              {
                path: ':threadId',
                element: <Thread.Individual />,
              },
              {
                path: 'create',
                element: <Thread.Create />,
              },
            ],
          },
        ],
      },
      {
        path: 'event',
        children: [
          {
            index: true,
            element: <Event.List />,
          },
          {
            path: ':id',
            element: <Event.Individual />,
            children: Form.Event.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <Event.Edit />,
            children: Form.Event.getRouterTabs(),
          },
          {
            path: 'create',
            element: <Event.Create />,
            children: Form.Event.getRouterTabs(),
          },
          {
            path: ':id/income/create',
            element: <Event.Income.Create />,
          },
          {
            path: ':id/cost/create',
            element: <Event.Cost.Create />,
          },
          {
            path: ':id/income/:eventIncomeId',
            element: <Event.Income.Individual />,
          },
          {
            path: ':id/income/edit/:eventIncomeId',
            element: <Event.Income.Edit />,
          },
          {
            path: ':id/cost/:eventCostId',
            element: <Event.Cost.Individual />,
          },
          {
            path: ':id/cost/edit/:eventCostId',
            element: <Event.Cost.Edit />,
          },
        ],
      },
      {
        path: 'channel',
        children: [
          {
            index: true,
            element: <Channel.List />,
          },
          {
            path: ':id',
            element: <Channel.Individual />,
            children: Form.Channel.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <Channel.Edit />,
            children: Form.Channel.getRouterTabs(),
          },
          {
            path: ':id/channel-video/edit/:videoId',
            element: <ChannelVideo.Edit />,
            children: Form.ChannelVideo.getRouterTabs(),
          },
          {
            path: ':id/channel-video/:videoId',
            element: <ChannelVideo.Individual />,
            children: Form.ChannelVideo.getRouterTabs(),
          },
        ],
      },

      {
        path: 'music-product-digital',
        children: [
          {
            index: true,
            element: <MusicProductDigital.List />,
          },
          {
            path: ':id',
            element: <MusicProductDigital.Individual />,
            children: Form.MusicProductDigital.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <MusicProductDigital.Edit />,
            children: Form.MusicProductDigital.getRouterTabs(),
          },
          {
            path: 'create',
            element: <MusicProductDigital.Create />,
            children: Form.MusicProductDigital.getRouterTabs(),
          },
        ],
      },
      {
        path: 'contracts',
        children: [
          {
            index: true,
            element: <Contract.List />,
          },
        ],
      },
      {
        path: 'contracts-to-expire',
        element: <Contract.ListToExpired />,
      },
      {
        path: 'templates-extended',
        children: [
          {
            index: true,
            element: <ContractTemplate.ExtendedList />,
          },
          {
            path: 'agreement',
            children: [
              {
                path: ':id',
                element: <ContractTemplate.Agreement.Individual />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'edit/:id',
                element: <ContractTemplate.Agreement.Edit />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'create',
                element: <ContractTemplate.Agreement.Create />,
                children: Form.Contract.getRouterTabs(),
              },
            ],
          },
          {
            path: 'digital-distribution',
            children: [
              {
                path: ':id',
                element: <ContractTemplate.DigitalDistribution.Individual />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'edit/:id',
                element: <ContractTemplate.DigitalDistribution.Edit />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'create',
                element: <ContractTemplate.DigitalDistribution.Create />,
                children: Form.Contract.getRouterTabs(),
              },
            ],
          },
          {
            path: 'physical-distribution',
            children: [
              {
                path: ':id',
                element: <ContractTemplate.PhysicalDistribution.Individual />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'edit/:id',
                element: <ContractTemplate.PhysicalDistribution.Edit />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'create',
                element: <ContractTemplate.PhysicalDistribution.Create />,
                children: Form.Contract.getRouterTabs(),
              },
            ],
          },
          {
            path: 'merch-distribution',
            children: [
              {
                path: ':id',
                element: <ContractTemplate.MerchDistribution.Individual />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'edit/:id',
                element: <ContractTemplate.MerchDistribution.Edit />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'create',
                element: <ContractTemplate.MerchDistribution.Create />,
                children: Form.Contract.getRouterTabs(),
              },
            ],
          },
          {
            path: 'channel-distribution',
            children: [
              {
                path: ':id',
                element: <ContractTemplate.ChannelDistribution.Individual />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'edit/:id',
                element: <ContractTemplate.ChannelDistribution.Edit />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'create',
                element: <ContractTemplate.ChannelDistribution.Create />,
                children: Form.Contract.getRouterTabs(),
              },
            ],
          },
          {
            path: 'section',
            children: [
              {
                path: ':id',
                element: <ContractTemplate.Section.Individual />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'edit/:id',
                element: <ContractTemplate.Section.Edit />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'create',
                element: <ContractTemplate.Section.Create />,
                children: Form.Contract.getRouterTabs(),
              },
            ],
          },
          {
            path: 'paragraph-section',
            children: [
              {
                path: ':id',
                element: <ContractTemplate.ParagraphSection.Individual />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'edit/:id',
                element: <ContractTemplate.ParagraphSection.Edit />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'create',
                element: <ContractTemplate.ParagraphSection.Create />,
                children: Form.Contract.getRouterTabs(),
              },
            ],
          },
        ],
      },
      {
        path: 'templates',
        children: [
          {
            index: true,
            element: <ContractTemplate.List />,
          },

          {
            path: 'contract',
            children: [
              {
                path: ':id',
                element: <ContractTemplate.Contract.Individual />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'edit/:id',
                element: <ContractTemplate.Contract.Edit />,
                children: Form.Contract.getRouterTabs(),
              },
              {
                path: 'create',
                element: <ContractTemplate.Contract.Create />,
                children: Form.Contract.getRouterTabs(),
              },
            ],
          },
        ],
      },
      {
        path: 'settlement',
        children: [
          {
            index: true,
            element: <Settlement.List />,
          },
        ],
      },
      {
        path: 'raport',
        children: [
          {
            index: true,
            element: <GlobalRaports />,
          },
        ],
      },

      {
        path: 'unpaid-advance',
        element: <Unpaid.Advance />,
      },
      {
        path: 'unpaid-cost',
        element: <Unpaid.Cost />,
      },
      {
        path: 'client-users',
        children: [
          {
            index: true,
            element: <ClientUser.List />,
          },
          {
            path: 'create',
            element: <ClientUser.Create />,
            children: Form.ClientUser.getRouterTabs(),
          },
          {
            path: ':id',
            element: <ClientUser.Individual />,
            children: Form.ClientUser.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <ClientUser.Edit />,
            children: Form.ClientUser.getRouterTabs(),
          },
        ],
      },
      {
        path: 'client-threads',
        element: <Thread.List />,
      },
      {
        path: 'client-account',
        children: [
          {
            index: true,
            element: <ClientAccount.List />,
          },
          {
            path: 'create',
            element: <ClientAccount.Create />,
            children: Form.ClientAccount.getRouterTabs(),
          },
          {
            path: ':id',
            element: <ClientAccount.Individual />,
            children: Form.ClientAccount.getRouterTabs(),
          },
          {
            path: 'edit/:id',
            element: <ClientAccount.Edit />,
            children: Form.ClientAccount.getRouterTabs(),
          },
        ],
      },
      {
        path: 'dev-tools',
        element: <DevToolsTemplate />,
        children: [
          {
            path: 'error-codes',
            element: <DevTools.ErrorCodes />,
          },
          {
            path: 'countries',
            element: <DevTools.Countries />,
          },
          {
            path: 'languages',
            element: <DevTools.Languages />,
          },
          {
            path: 'supported-languages',
            element: <DevTools.SupportedLanguages />,
          },
          {
            path: 'delivery-status',
            element: <DevTools.DeliveryStatus />,
          },
        ],
      },
      {
        path: 'settings',
        element: <SettingsTemplate />,
        children: [
          {
            index: true,
            element: <Settings.User />,
          },
          {
            path: 'application',
            element: <Settings.Application />,
          },
          {
            path: 'client-module-configuration',
            element: <Settings.ClientModule />,
          },
          {
            path: 'email',
            children: [
              {
                index: true,
                element: <Email.List />,
              },
              {
                path: ':id',
                element: <Email.Individual />,
              },
              {
                path: 'edit/:id',
                element: <Email.Edit />,
              },
              {
                path: 'create',
                element: <Email.Create />,
              },
            ],
          },
          {
            path: 'license',
            element: <Settings.License />,
          },
          {
            path: 'notifications',
            element: <Settings.Notification />,
          },
          {
            path: 'digital-delivery-template',
            children: [
              {
                index: true,
                element: <Settings.DigitalDeliveryTemplate.Detail />,
              },
              {
                path: 'create',
                element: <Settings.DigitalDeliveryTemplate.Create />,
              },
              {
                path: 'edit',
                element: <Settings.DigitalDeliveryTemplate.Edit />,
              },
            ],
          },

          {
            path: 'sales-channel',
            children: [
              {
                index: true,
                element: <SaleChannel.List />,
              },
              {
                path: ':id',
                element: <SaleChannel.Individual />,
                children: Form.SaleChannel.getRouterTabs?.() ?? [],
              },
              {
                path: 'edit/:id',
                element: <SaleChannel.Edit />,
                children: Form.SaleChannel.getRouterTabs?.() ?? [],
              },
              {
                path: 'create',
                element: <SaleChannel.Create />,
              },
            ],
          },
          {
            path: 'provider',
            children: [
              {
                index: true,
                element: <Provider.List />,
              },
              {
                path: ':id',
                children: [
                  { index: true, element: <Provider.Individual /> },
                  {
                    path: 'ftp-connection',
                    children: [
                      {
                        path: ':salesChannelId',
                        element: <FtpConnection.Individual />,
                        children: Form.FtpConnection.getRouterTabs(),
                      },
                      {
                        path: 'edit/:salesChannelId',
                        element: <FtpConnection.Edit />,
                        children: Form.FtpConnection.getRouterTabs(),
                      },
                      {
                        path: 'create/:salesChannelId',
                        element: <FtpConnection.Create />,
                        children: Form.FtpConnection.getRouterTabs(),
                      },
                    ],
                  },
                  {
                    path: 'sftp-connection',
                    children: [
                      {
                        path: ':salesChannelId',
                        element: <SftpConnection.Individual />,
                        children: Form.SftpConnection.getRouterTabs(),
                      },
                      {
                        path: 'edit/:salesChannelId',
                        element: <SftpConnection.Edit />,
                        children: Form.SftpConnection.getRouterTabs(),
                      },
                      {
                        path: 'create/:salesChannelId',
                        element: <SftpConnection.Create />,
                        children: Form.SftpConnection.getRouterTabs(),
                      },
                    ],
                  },
                  {
                    path: 'blob-connection',
                    children: [
                      {
                        path: ':salesChannelId',
                        element: <BlobConnection.Individual />,
                        children: Form.BlobConnection.getRouterTabs(),
                      },
                      {
                        path: 'edit/:salesChannelId',
                        element: <BlobConnection.Edit />,
                        children: Form.BlobConnection.getRouterTabs(),
                      },
                      {
                        path: 'create/:salesChannelId',
                        element: <BlobConnection.Create />,
                        children: Form.BlobConnection.getRouterTabs(),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'edit/:id',
                element: <Provider.Edit />,
              },
              {
                path: 'create',
                element: <Provider.Create />,
              },
            ],
          },
          {
            path: 'settlement-periods',
            children: [
              {
                index: true,
                element: <SettlementPeriod.List />,
              },
            ],
          },
          {
            path: 'users',
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <User.List />,
              },
              {
                path: 'create',
                element: <User.Create />,
                children: Form.User.getRouterTabs(),
              },
              {
                path: ':id',
                element: <User.Individual />,
                children: Form.User.getRouterTabs(),
              },
              {
                path: 'edit/:id',
                element: <User.Edit />,
                children: Form.User.getRouterTabs(),
              },
            ],
          },
          {
            path: 'serviceCode',
            element: <ServiceCode />,
          },
          {
            path: 'logs',
            children: [
              {
                index: true,
                element: <Logs />,
              },
              {
                path: 'digital-delivery-errors',
                element: <DigitalDeliveryErrors />,
              },
            ],
          },
          {
            path: 'demo',
            element: <Settings.Demo />,
          },
        ],
      },
    ],
  },
  {
    element: (
      <LogoProvider>
        <PublicRoute />
      </LogoProvider>
    ),
    children: [
      { path: '*', element: <Navigate to='/login' /> },
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'reset-password',
        children: [
          {
            index: true,
            element: <Password.Reset />,
          },
          {
            path: 'request',
            element: <Password.Request />,
          },
          {
            path: 'resend',
            element: <Password.RequestAgain />,
          },
          {
            path: 'confirmation',
            element: <Password.Confirmation />,
          },
        ],
      },
      {
        path: 'confirm-email',
        children: [
          {
            index: true,
            element: <ConfirmEmail.Confirm />,
          },
          {
            path: 'resend',
            element: <ConfirmEmail.ResendConfirm />,
          },
        ],
      },
      {
        path: 'two-factor',
        children: [
          { index: true, element: <TwoFactor /> },
          {
            path: ':token/:email',
            children: [
              { index: true, element: <TwoFactor /> }, //when user pass code from email
              { path: ':code', element: <TwoFactor /> }, //when user click on email link
            ],
          },
        ],
      },
      {
        path: 'service-code/:token',
        element: <ServiceLogin />,
      },
      {
        path: 'two-factor-recovery',
        element: <TwoFactorRecovery />,
      },
      {
        path: 'update-label',
        element: <LabelForm />,
      },
    ],
  },
  {
    path: 'health',
    element: <Health />,
  },
  { path: 'version' },
]);
